import { toast } from "@/components/common/Toast/Toast"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import * as React from "react"
import { toastApiError } from "../../../../../api/apiError"
import { useAssignDeclarationSupplier } from "../../../../../api/declarationsApi"
import {
  createSupplierErrorDetailOf,
  useCreateSupplier,
  useSupplierList,
} from "../../../../../api/suppliersApi"
import type { SupplierRow } from "../../../../../api/types/masterData"
import { suggestSupplierNumber } from "./supplierNumberSuggestion"

/** Reads a 409's `detail.code` off the assign-supplier endpoint's error —
 *  the SAME `{code, message}` envelope `createSupplierErrorDetailOf` (in
 *  suppliersApi.ts) reads for the sibling create endpoint, read locally here
 *  rather than imported since `useAssignDeclarationSupplier` (declarationsApi.ts)
 *  defines no typed detail of its own to hang a shared reader off. */
function assignErrorCodeOf(error: unknown): string | null {
  const detail = (
    error as { response?: { data?: { detail?: { code?: string } } } }
  )?.response?.data?.detail
  return detail?.code ?? null
}

/** `assign_supplier`'s MC_SUPPLIER_REASSIGN refusals: nothing was written and
 *  assigning by hand would be refused the same way, so one right after an inline
 *  create is told as itself, never as "created, assign it by hand". Only these
 *  two: NOT_PROCESSED and ALREADY_APPROVED reach main too and keep its message. */
const ASSIGN_REFUSALS = new Set([
  "HAS_REVIEW_DECISIONS",
  "RECOMPUTE_IN_PROGRESS",
])

export interface SupplierAssignControlProps {
  declId: string
  /** The document's own verbatim issuer name (`useStudioV2State`'s own
   *  `supplierMentioned`) — the create form's Name prefill source. */
  supplierMentioned: string | null
  /** Fallback prefill when the document names no issuer at all. */
  supplierName?: string | null
  /** The popover's own trigger — callers style it differently (the
   *  breadcrumb's amber chip vs. the approve gate's inline button), so this
   *  stays a plain slot rather than a hard-coded button. */
  trigger: React.ReactNode
  /** Fires after a successful assign — existing supplier, newly created one,
   *  or the SUPPLIER_NUMBER_TAKEN "assign instead" — with the bound
   *  supplier's id. Optional: every pre-existing call site (chip, mask
   *  banner, approve gate, open-points map) omits it and keeps its current
   *  behaviour unchanged. The mandatory supplier gate is the one caller that
   *  needs to know assignment landed, so it can trigger the re-extraction
   *  that recomputes scope/verdicts against the new supplier's articles. */
  onAssigned?: (supplierId: string) => void
  /** Veto point BEFORE any write. Awaited with the supplier the reviewer just
   *  picked (`id` is null for a supplier about to be created, which does not
   *  exist yet); resolving false aborts the whole gesture, nothing is created
   *  or assigned, and the popover closes. Optional: every pre-existing call
   *  site omits it and keeps its current behaviour unchanged. The Studio V2
   *  "change supplier" control is the one caller, for the same-supplier no-op
   *  and the re-read confirmation (`SupplierReassignControl`). */
  beforeAssign?: (supplier: {
    id: string | null
    name: string
  }) => Promise<boolean>
}

type Step = "search" | "create"

/**
 * The unmatched-supplier escape hatch: search existing master data and
 * assign, or fall through to an inline two-step create-then-assign form —
 * the reviewer must never dead-end on a declaration whose issuer master data
 * does not know yet (approve() 409s NO_SUPPLIER, raising a Rückfrage 422s it
 * too).
 *
 * Markup follows ArticleSlot.tsx's RemapPopover — ui/popover + plain input +
 * button list (sv2-mappop/sv2-mh/sv2-acin/sv2-acempty/sv2-acitem) — with two
 * deliberate departures: `disablePortal` (RemapPopover portals to
 * document.body by default, where `.sv2-root`'s scoped CSS variables do not
 * resolve — see TagDocumentShared.tsx for the same escape hatch used for the
 * same reason), and a NEW `sv2-suplist` wrapper instead of `sv2-maplist`
 * (that class's own rule forces the row's name span into mono, right for an
 * article number, wrong for a company name).
 */
export function SupplierAssignControl({
  declId,
  supplierMentioned,
  supplierName,
  trigger,
  onAssigned,
  beforeAssign,
}: SupplierAssignControlProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [step, setStep] = React.useState<Step>("search")
  const [query, setQuery] = React.useState("")
  const [name, setName] = React.useState("")
  const [supplierNumber, setSupplierNumber] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [country, setCountry] = React.useState("")
  const [conflict, setConflict] = React.useState<{
    supplierId: string
    message: string
  } | null>(null)
  // Create's 409 SUPPLIER_NUMBER_TAKEN can name a SOFT-DELETED supplier (by
  // design — see `handleAssignConflict`'s own doc), so "assign instead" can
  // itself 409 SUPPLIER_NOT_FOUND: a dead end with only a toast, since the
  // reviewer can neither create under that number (still taken) nor assign
  // to it (gone). True once that happens, until the reviewer edits the
  // number (their actual escape) or leaves this conflict behind.
  const [conflictDeleted, setConflictDeleted] = React.useState(false)

  const { data: searchPages, isFetching: searching } = useSupplierList({
    q: query || undefined,
    limit: 50,
  })
  const results = React.useMemo(
    () => searchPages?.pages.flatMap((p) => p.items) ?? [],
    [searchPages],
  )

  // Duplicate-name SOFT check for the create step — never blocks (a
  // same-named new supplier can be genuine), only offers a one-click
  // shortcut if the reviewer is about to recreate an existing row.
  //
  // `isPlaceholderData` MATTERS HERE, unlike the search list above:
  // `useSupplierList` opts into `keepPreviousData` (mcQueryHelpers.ts), so
  // the instant `name` changes (`openCreateStep` setting it from ""), this
  // query shows the PREVIOUS observer's rows — an unfiltered top-3 from the
  // very first (mount-time, `name === ""`) fetch — while the real one is in
  // flight. `dupResults` must read empty during that window: a stale row
  // still renders with a live one-click assign, and the whole point of this
  // hint is never to hand the reviewer a supplier unrelated to what they
  // typed.
  const { data: dupPages, isPlaceholderData: dupStale } = useSupplierList({
    q: name || undefined,
    limit: 3,
  })
  const dupResults = React.useMemo(
    () =>
      step === "create" && !dupStale
        ? (dupPages?.pages.flatMap((p) => p.items) ?? [])
        : [],
    [dupPages, dupStale, step],
  )

  const assignSupplier = useAssignDeclarationSupplier()
  const createSupplier = useCreateSupplier()
  const submitting = createSupplier.isPending || assignSupplier.isPending

  const resetToSearch = () => {
    setStep("search")
    setQuery("")
    setConflict(null)
    setConflictDeleted(false)
  }

  const closeAndReset = () => {
    setOpen(false)
    resetToSearch()
  }

  // True while the caller's veto point (`beforeAssign`) is open, e.g. its
  // confirmation dialog, which portals OUT of this popover. Focus moving into
  // it counts as an interaction outside and would dismiss the popover, which
  // resets the form: a conflict the create then reports would land in a form
  // nobody can see. Only ever set when `beforeAssign` is passed.
  const vetoPending = React.useRef(false)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    // NO SEED FROM MEMORY. The picker opens exactly as it did before memory existed —
    // an empty search box, independent of whatever memory is suggesting above it. If
    // the suggestion is wrong, a pre-filled box looked pre-filtered to a possibly-wrong
    // guess rather than the full picker a reviewer expects from a blank search.
    if (!next) resetToSearch()
  }

  const onAssignError = (error: unknown) => {
    // A reassignment over reviewer decisions is refused with nothing written
    // (MC_SUPPLIER_REASSIGN). Not a failure to retry: the reviewer resets the
    // decisions first, so it is a warning that names that step, never the
    // generic "could not be assigned" error.
    if (assignErrorCodeOf(error) === "HAS_REVIEW_DECISIONS") {
      toast.warning(translate("MC_DECL_V2_SUP_CHANGE_HAS_DECISIONS"))
      return
    }
    toastApiError(error, translate, "MC_DECL_V2_SUP_ASSIGN_FAILED")
  }

  /** The caller's veto point. Resolves false (and closes the popover) when the
   *  reviewer declined or picked what is already bound: nothing is left to do
   *  in it. A rejection counts as a veto. */
  const allowed = async (supplier: {
    id: string | null
    name: string
  }): Promise<boolean> => {
    let ok: boolean
    vetoPending.current = true
    try {
      ok = !!beforeAssign && (await beforeAssign(supplier))
    } catch {
      ok = false
    } finally {
      vetoPending.current = false
    }
    if (!ok) closeAndReset()
    return ok
  }

  /** Runs `write` straight away when no veto point was handed in (the path
   *  every pre-existing caller takes, synchronously, exactly as before), else
   *  only once the veto point lets it through. */
  const gated = (
    supplier: { id: string | null; name: string },
    write: () => void,
  ) => {
    if (!beforeAssign) {
      write()
      return
    }
    void allowed(supplier).then((ok) => {
      if (ok) write()
    })
  }

  const assignSelected = (supplier: Pick<SupplierRow, "id" | "name">) => {
    assignSupplier.mutate(
      { declId, supplierId: supplier.id },
      {
        onSuccess: () => {
          toast.success(
            translate("MC_DECL_V2_SUP_ASSIGNED", { name: supplier.name }),
          )
          closeAndReset()
          onAssigned?.(supplier.id)
        },
        onError: onAssignError,
      },
    )
  }

  const handleSelect = (supplier: Pick<SupplierRow, "id" | "name">) => {
    gated({ id: supplier.id, name: supplier.name }, () =>
      assignSelected(supplier),
    )
  }

  const openCreateStep = () => {
    const prefill = supplierMentioned ?? supplierName ?? ""
    setName(prefill)
    setSupplierNumber(
      suggestSupplierNumber(
        prefill,
        results.map((s) => s.supplierNumber),
      ),
    )
    setEmail("")
    setCountry("")
    setConflict(null)
    setStep("create")
  }

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedNumber = supplierNumber.trim()
    if (!trimmedName || !trimmedNumber || submitting) return
    setConflict(null)
    setConflictDeleted(false)
    if (beforeAssign && !(await allowed({ id: null, name: trimmedName }))) {
      return
    }

    let supplier: { id: string; name: string }
    try {
      supplier = await createSupplier.mutateAsync({
        supplierNumber: trimmedNumber,
        name: trimmedName,
        email: email.trim() || undefined,
        country: country.trim() || undefined,
      })
    } catch (err) {
      const detail = createSupplierErrorDetailOf(err)
      if (detail?.code === "SUPPLIER_NUMBER_TAKEN" && detail.supplier_id) {
        setConflict({
          supplierId: detail.supplier_id,
          message: translate("MC_DECL_V2_SUP_NUMBER_TAKEN"),
        })
      } else if (detail) {
        toastApiError(err, translate, "MC_DECL_V2_SUP_CREATE_FAILED")
      }
      // No `else`: a >=500 is already toasted once by the global interceptor.
      return
    }

    try {
      await assignSupplier.mutateAsync({ declId, supplierId: supplier.id })
    } catch (error) {
      const code = assignErrorCodeOf(error)
      if (code && ASSIGN_REFUSALS.has(code)) {
        onAssignError(error)
        return
      }
      // The supplier now EXISTS (created above) but assignment itself
      // failed — tell the reviewer rather than silently losing the row; it
      // is now findable from the search step. Shown for every failure but a
      // refusal (above), even on a >=500 the global interceptor already
      // toasted once: that toast is a generic "server error" notice, never
      // THIS row's recovery instruction, so dropping it on a 500 would
      // silently strand a freshly created supplier with nobody told to go
      // find it.
      // `toast.warning` (not `.error`), deliberately, so it never reads as a
      // SECOND failure stacked on the interceptor's own error toast — only
      // as the recovery step it actually is.
      toast.warning(translate("MC_DECL_V2_SUP_ASSIGN_AFTER_CREATE_FAILED"))
      return
    }
    toast.success(
      translate("MC_DECL_V2_SUP_CREATED_ASSIGNED", { name: supplier.name }),
    )
    closeAndReset()
    onAssigned?.(supplier.id)
  }

  const handleAssignConflict = () => {
    if (!conflict) return
    const supplierId = conflict.supplierId
    setConflictDeleted(false)
    gated({ id: supplierId, name: "" }, () => assignConflicting(supplierId))
  }

  const assignConflicting = (supplierId: string) => {
    assignSupplier.mutate(
      { declId, supplierId },
      {
        onSuccess: () => {
          toast.success(translate("MC_DECL_V2_SUP_CONFLICT_ASSIGNED"))
          closeAndReset()
          onAssigned?.(supplierId)
        },
        onError: (error) => {
          // The number's existing holder is a SOFT-DELETED supplier — see
          // `conflictDeleted`'s own doc. A content-framed hint replaces the
          // now-useless "assign instead" button rather than only a toast: it
          // names the actual escape (edit the number above) instead of
          // leaving the reviewer to guess why retrying keeps failing.
          if (assignErrorCodeOf(error) === "SUPPLIER_NOT_FOUND") {
            setConflictDeleted(true)
            return
          }
          onAssignError(error)
        },
      },
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        disablePortal
        align="start"
        className="w-[296px] rounded-none border-0 bg-transparent p-0 shadow-none"
        onClick={(e) => e.stopPropagation()}
        onInteractOutside={(e) => {
          if (vetoPending.current) e.preventDefault()
        }}
      >
        <div className="sv2-mappop" style={{ position: "static" }}>
          {step === "search" ? (
            <>
              <div className="sv2-mh">
                {translate("MC_DECL_V2_SUP_SEARCH_HEADER")}
              </div>
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={translate("MC_DECL_V2_SUP_SEARCH_PLACEHOLDER")}
                className="sv2-acin"
              />
              <div className="sv2-suplist">
                {!searching && results.length === 0 && (
                  <p className="sv2-acempty">
                    {translate("MC_DECL_V2_SUP_SEARCH_EMPTY")}
                  </p>
                )}
                {results.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleSelect(s)}
                    className="sv2-acitem w-full text-left"
                  >
                    <span className="sv2-an">{s.name}</span>
                    <span className="sv2-apo">{s.supplierNumber}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={openCreateStep}
                className="sv2-mapfree w-full text-left hover:text-attention"
              >
                {translate("MC_DECL_V2_SUP_CREATE_NEW")}
              </button>
            </>
          ) : (
            <form onSubmit={handleCreateSubmit}>
              <div className="sv2-mh flex items-center justify-between">
                <span>{translate("MC_DECL_V2_SUP_CREATE_HEADER")}</span>
                <button
                  type="button"
                  onClick={resetToSearch}
                  className="sv2-fwall"
                >
                  {translate("MC_DECL_V2_SUP_BACK")}
                </button>
              </div>
              <div className="sv2-supfield">
                <label htmlFor="sv2-sup-name">
                  {translate("MC_DECL_V2_SUP_FIELD_NAME")}
                </label>
                <input
                  id="sv2-sup-name"
                  className="sv2-acin"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="sv2-supfield">
                <label htmlFor="sv2-sup-number">
                  {translate("MC_DECL_V2_SUP_FIELD_NUMBER")}
                </label>
                <input
                  id="sv2-sup-number"
                  className="sv2-acin"
                  value={supplierNumber}
                  onChange={(e) => {
                    setSupplierNumber(e.target.value)
                    // Editing the number IS the escape from a
                    // conflictDeleted dead end — clear the stale hint the
                    // moment the reviewer acts on it, rather than leaving it
                    // glued to a number they already changed.
                    setConflictDeleted(false)
                  }}
                  required
                />
              </div>
              <div className="sv2-supfield">
                <label htmlFor="sv2-sup-email">
                  {translate("MC_DECL_V2_SUP_FIELD_EMAIL")}
                </label>
                <input
                  id="sv2-sup-email"
                  type="email"
                  className="sv2-acin"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="sv2-supfield">
                <label htmlFor="sv2-sup-country">
                  {translate("MC_DECL_V2_SUP_FIELD_COUNTRY")}
                </label>
                <input
                  id="sv2-sup-country"
                  className="sv2-acin"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
              {dupResults.length > 0 && (
                <div className="sv2-artnote">
                  <div>
                    {translate("MC_DECL_V2_SUP_DUP_HINT")}
                    <div className="mt-1.5 flex flex-col gap-1">
                      {dupResults.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => handleSelect(s)}
                          className="sv2-acitem w-full text-left"
                        >
                          <span className="sv2-an">{s.name}</span>
                          <span className="sv2-apo">{s.supplierNumber}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {conflict && (
                <div className="sv2-supconflict" role="alert">
                  <p>{conflict.message}</p>
                  {conflictDeleted ? (
                    // The conflicting supplier is soft-deleted: "assign
                    // instead" just 409s again, so it is replaced (not
                    // merely supplemented) by the honest escape — edit the
                    // number field above.
                    <p>
                      {/* NEW KEY, proposed in NEW-KEYS-W1.md — not yet in
                          translations_*.ts (out of this pass's allowed
                          files). Cast keeps `tsc` clean; `translate` itself
                          falls back to the bare key at runtime until the key
                          lands, same as every other not-yet-catalogued key
                          in this codebase. */}
                      {translate("MC_DECL_V2_SUP_CONFLICT_DELETED")}
                    </p>
                  ) : (
                    <button type="button" onClick={handleAssignConflict}>
                      {translate("MC_DECL_V2_SUP_CONFLICT_ASSIGN_INSTEAD")}
                    </button>
                  )}
                </div>
              )}
              <div className="flex justify-end p-3">
                <button
                  type="submit"
                  disabled={
                    !name.trim() || !supplierNumber.trim() || submitting
                  }
                  className="sv2-btn sv2-btn-primary sv2-btn-sm"
                >
                  {translate("MC_DECL_V2_SUP_CREATE_SUBMIT")}
                </button>
              </div>
            </form>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
